// What an item's launch control says and where it goes (decisions 10–12).
//
// The URL always comes from the API (`launch_url`, or the allowlisted
// `external_url`); nothing here builds one. The label names the action and
// the target so the student can see where the click goes.
//
// launch_target_missing:
//   true   the target is gone; launch_url is the app's front page, so the
//          button names the app and the row shows a notice.
//   null   the check could not run; launch_url is also the app's front page,
//          so the button names the app, with no warning (null is "unknown",
//          not "missing").
//   false  the target resolved; the button names the action and the target.

const TARGET_VERBS = {
  topic: 'Practise topic',
  problem: 'Open problem',
  interview_type: 'Start mock interview',
  quiz_topic: 'Open quiz',
};

export function appDisplayName(capabilityKey, capabilityNames) {
  const name = capabilityNames && capabilityNames[capabilityKey];
  return typeof name === 'string' && name.trim() ? name.trim() : 'the app';
}

export function externalHostname(url) {
  if (typeof url !== 'string') return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return null;
    return parsed.hostname.replace(/^www\./, '') || null;
  } catch {
    return null;
  }
}

function targetText(target) {
  const label = typeof target.label === 'string' && target.label.trim() ? target.label.trim() : null;
  const ref = typeof target.ref === 'string' && target.ref.trim() ? target.ref.trim() : null;
  const text = label || ref;
  if (!text) return null;
  return target.kind === 'quiz_topic' ? `“${text}”` : text;
}

// Returns null for a manual item (no app, no link) or an app the catalogue no
// longer has (launch_url null). Otherwise:
//   { kind: 'external', href, label, hostname }
//   { kind: 'app', href, label, appName, targetMissing }
export function launchButton(item, capabilityNames) {
  if (!item) return null;
  if (!item.capability_key) {
    const hostname = externalHostname(item.external_url);
    if (!hostname) return null;
    return { kind: 'external', href: item.external_url, label: `Open ${hostname} ↗`, hostname };
  }
  if (typeof item.launch_url !== 'string' || !item.launch_url.startsWith('/')) return null;

  const appName = appDisplayName(item.capability_key, capabilityNames);
  const target = item.launch_target;
  const verb = target ? TARGET_VERBS[target.kind] : null;
  const text = target ? targetText(target) : null;
  const resolved = item.launch_target_missing === false;

  if (target && resolved && verb && text) {
    return { kind: 'app', href: item.launch_url, label: `${verb} · ${text}`, appName, targetMissing: false };
  }
  return {
    kind: 'app',
    href: item.launch_url,
    label: `Open ${appName}`,
    appName,
    targetMissing: Boolean(target) && item.launch_target_missing === true,
  };
}
